import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';
import { useViewMode } from '../contexts/ViewModeContext';
import { Search, Filter, Plus, Edit, Trash2, X, Users, Briefcase, Eye, UserPlus, ChevronDown, Check, Palette, Tag, FolderOpen, Key, Archive, ArchiveRestore, AlertTriangle, Clock, LayoutGrid, BarChart3, GitMerge, ArrowRight, Shield, Loader2, Grid3x3 } from 'lucide-react';
import { CLIENT_COLOR_PALETTE, ClientColorBadge, getLighterColor } from '../lib/clientColors';
import ClientDetailsModal from './ClientDetailsModal';
import ClientDataGrid from './ClientDataGrid';
import { ClientArchiveModal, ClientRestoreModal, ExpiringRetainersAlert } from './ClientArchiveModals';
import ClientDistributionManager from './ClientDistributionManager';
import EmptyState from './EmptyState';
import { formatDate } from '../lib/dateUtils';
import { HeaderSkeleton, CardGridSkeleton } from './skeletons/Skeletons';

// Category Modal Component
function CategoryModal({ isOpen, onClose, category, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || ''
      });
    } else {
      setFormData({ name: '', description: '' });
    }
  }, [category, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      // Error handled in parent
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-md mx-4 shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-white/40">
          <h2 className="text-lg font-semibold text-[#1a1a2e]">
            {category ? 'Edit Category' : 'New Category'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded-xl">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Technology, Healthcare"
              className="w-full px-3 py-2 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
              data-testid="category-name-input"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional description for this category"
              rows={3}
              className="w-full px-3 py-2 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
              data-testid="category-description-input"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-700 border border-white/40 rounded-xl hover:bg-white/60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700 disabled:opacity-50"
              data-testid="save-category-btn"
            >
              {saving ? 'Saving...' : (category ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Searchable Employee Dropdown Component
function EmployeeSearchDropdown({ employees, value, onChange, placeholder = "Search and select employee..." }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredEmployees = employees.filter(emp => {
    const name = emp.personal?.full_name || emp.work_email || '';
    const email = emp.work_email || '';
    const designation = emp.designation || emp.employment?.designation || '';
    const search = searchTerm.toLowerCase();
    return name.toLowerCase().includes(search) || 
           email.toLowerCase().includes(search) ||
           designation.toLowerCase().includes(search);
  });

  const selectedEmployee = employees.find(emp => emp._id === value);
  const displayValue = selectedEmployee 
    ? (selectedEmployee.personal?.full_name || selectedEmployee.work_email)
    : '';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 py-2 text-sm text-left border rounded-xl bg-white flex items-center justify-between ${
          isOpen ? 'ring-2 ring-pink-500 border-pink-500' : 'border-white/40'
        }`}
      >
        <span className={displayValue ? 'text-[#1a1a2e]' : 'text-neutral-400'}>
          {displayValue || placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-neutral-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-[9999] w-64 mt-1 glass-card rounded-xl shadow-xl right-0">
          {/* Search Input */}
          <div className="p-2 border-b border-white/30">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
                autoFocus
              />
            </div>
          </div>

          {/* Employee List */}
          <div className="max-h-48 overflow-y-auto">
            {/* Clear Selection Option */}
            <button
              type="button"
              onClick={() => {
                onChange('');
                setIsOpen(false);
                setSearchTerm('');
              }}
              className="w-full px-3 py-2 text-left text-sm text-neutral-500 hover:bg-white/60 flex items-center gap-2 border-b border-white/30"
            >
              <X className="w-4 h-4" />
              Clear selection
            </button>
            
            {filteredEmployees.length === 0 ? (
              <EmptyState icon={Users} title="No employees found" size="small" testId="empty-employees-search" />
            ) : (
              filteredEmployees.map((emp) => (
                <button
                  key={emp._id}
                  type="button"
                  onClick={() => {
                    onChange(emp._id);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-pink-50 flex items-center justify-between ${
                    value === emp._id ? 'bg-pink-50' : ''
                  }`}
                >
                  <div>
                    <p className="font-medium text-[#1a1a2e]">
                      {emp.personal?.full_name || emp.work_email}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {emp.designation || emp.employment?.designation || 'No designation'}
                    </p>
                  </div>
                  {value === emp._id && (
                    <Check className="w-4 h-4 text-pink-600" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminClientsPage() {
  const { isMobileView } = useViewMode();
  const [activeTab, setActiveTab] = useState('clients'); // 'clients', 'categories', 'archived', or 'distribution'
  const [clients, setClients] = useState([]);
  const [archivedClients, setArchivedClients] = useState([]);
  const [expiringRetainers, setExpiringRetainers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [managedCategories, setManagedCategories] = useState([]); // From client_categories collection
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showClientModal, setShowClientModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientUsers, setClientUsers] = useState([]);
  const [clientDetails, setClientDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Archive state
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveClient, setArchiveClient] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreClient, setRestoreClient] = useState(null);
  const [dismissedExpiring, setDismissedExpiring] = useState([]);
  
  // Category management state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  
  // Deduplication state
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [mergingPair, setMergingPair] = useState(null); // {source_id, target_id}
  const [clientForm, setClientForm] = useState({
    name: '',
    retainer: '',
    currency: 'INR',
    category: '',
    departments: [],
    pocs: {},
    color: '',
    // Enhanced client fields
    company_name: '',
    company_address: '',
    company_state: '',
    company_pin_code: '',
    gstin: '',
    pan: '',
    billing_cycle: 'monthly',
    payment_type: 'postpaid',
    retainer_start_date: '',
    retainer_end_date: '',
    brand_anniversary_date: ''
  });

  // Indian States and Union Territories for GST
  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
    'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    // Union Territories
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
  ];

  const currencies = [
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' }
  ];

  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: ''
  });

  const [showCategoryInput, setShowCategoryInput] = useState(false);

  useEffect(() => {
    fetchClients();
    fetchArchivedClients();
    fetchExpiringRetainers();
    fetchCategories();
    fetchManagedCategories();
    fetchDepartments();
    fetchEmployees();
  }, [categoryFilter]);

  const fetchClients = async () => {
    try {
      const params = {};
      if (categoryFilter) params.category = categoryFilter;
      if (searchQuery) params.search = searchQuery;
      
      const response = await adminApi.get('/api/admin/clients', { params });
      setClients(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setLoading(false);
    }
  };

  const fetchArchivedClients = async () => {
    try {
      const response = await adminApi.get('/api/admin/clients/archived');
      setArchivedClients(response.data?.clients || []);
    } catch (error) {
      console.error('Error fetching archived clients:', error);
    }
  };

  const fetchExpiringRetainers = async () => {
    try {
      const response = await adminApi.get('/api/admin/clients/expiring-retainers', { params: { days: 30 } });
      setExpiringRetainers(response.data?.clients || []);
    } catch (error) {
      console.error('Error fetching expiring retainers:', error);
    }
  };

  const fetchDuplicates = async () => {
    setDuplicatesLoading(true);
    try {
      const response = await adminApi.get('/api/admin/clients/duplicates');
      setDuplicateGroups(response.data?.duplicate_groups || []);
    } catch (error) {
      console.error('Error fetching duplicates:', error);
    } finally {
      setDuplicatesLoading(false);
    }
  };

  const handleMergeClients = async (sourceId, targetId) => {
    if (!window.confirm('Are you sure you want to merge these clients? This action cannot be undone. All tasks, invoices, and users will be moved to the kept client.')) return;
    
    setMergingPair({ source_id: sourceId, target_id: targetId });
    try {
      const response = await adminApi.post('/api/admin/clients/merge', {
        source_id: sourceId,
        target_id: targetId
      });
      toast.success(response.data?.message || 'Clients merged successfully');
      fetchClients();
      fetchArchivedClients();
      fetchDuplicates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge clients');
      console.error('Error:', error);
    } finally {
      setMergingPair(null);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await adminApi.get('/api/admin/clients/categories');
      setCategories(response.data?.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchManagedCategories = async () => {
    try {
      const response = await adminApi.get('/api/admin/client-categories');
      const data = response.data || [];
      setManagedCategories(data);
      // Auto-seed if collection is empty (first-time setup)
      if (data.length === 0) {
        seedCategories();
      }
    } catch (error) {
      console.error('Error fetching managed categories:', error);
      seedCategories();
    }
  };

  const seedCategories = async () => {
    try {
      await adminApi.post('/api/admin/client-categories/seed');
      const response = await adminApi.get('/api/admin/client-categories');
      setManagedCategories(response.data || []);
    } catch (error) {
      console.error('Error seeding categories:', error);
    }
  };

  const handleSaveCategory = async (formData) => {
    try {
      if (editingCategory) {
        await adminApi.put(`/api/admin/client-categories/${editingCategory._id}`, formData);
        toast.success('Category updated successfully');
      } else {
        await adminApi.post('/api/admin/client-categories', formData);
        toast.success('Category created successfully');
      }
      fetchManagedCategories();
      fetchCategories(); // Refresh the dropdown list too
      setShowCategoryModal(false);
      setEditingCategory(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
      throw error;
    }
  };

  const handleDeleteCategory = async (category) => {
    const clientCount = clients.filter(c => c.category === category.name).length;
    const confirmMsg = clientCount > 0 
      ? `This category is used by ${clientCount} client(s). They will keep their category but it won't appear in dropdowns. Continue?`
      : 'Are you sure you want to delete this category?';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await adminApi.delete(`/api/admin/client-categories/${category._id}`);
      toast.success('Category deleted');
      fetchManagedCategories();
      fetchCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await adminApi.get('/api/departments');
      setDepartments(response.data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await adminApi.get('/api/admin/employees');
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleSearch = () => {
    fetchClients();
  };

  const handleCreateClient = async () => {
    if (!clientForm.name || !clientForm.category) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const dataToSend = {
        ...clientForm,
        retainer: clientForm.retainer ? parseFloat(clientForm.retainer) : null,
        currency: clientForm.currency || 'INR'
      };
      
      await adminApi.post('/api/admin/clients', dataToSend);
      toast.success('Client created successfully');
      fetchClients();
      fetchCategories();
      setShowClientModal(false);
      resetClientForm();
    } catch (error) {
      toast.error('Failed to create client');
      console.error('Error:', error);
    }
  };

  const handleUpdateClient = async () => {
    try {
      const dataToSend = {
        ...clientForm,
        retainer: clientForm.retainer ? parseFloat(clientForm.retainer) : null,
        currency: clientForm.currency || 'INR'
      };
      
      await adminApi.put(`/api/admin/clients/${editingClient._id}`, dataToSend);
      toast.success('Client updated successfully');
      fetchClients();
      fetchCategories();
      setShowClientModal(false);
      setEditingClient(null);
      resetClientForm();
    } catch (error) {
      toast.error('Failed to update client');
      console.error('Error:', error);
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    
    try {
      await adminApi.delete(`/api/admin/clients/${clientId}`);
      toast.success('Client deleted successfully');
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
      console.error('Error:', error);
    }
  };

  const handleDepartmentToggle = (deptName) => {
    const isSelected = clientForm.departments.includes(deptName);
    
    if (isSelected) {
      const newDepts = clientForm.departments.filter(d => d !== deptName);
      const newPocs = { ...clientForm.pocs };
      delete newPocs[deptName];
      setClientForm({ ...clientForm, departments: newDepts, pocs: newPocs });
    } else {
      setClientForm({ 
        ...clientForm, 
        departments: [...clientForm.departments, deptName] 
      });
    }
  };

  const handlePOCSelect = (department, employeeId) => {
    setClientForm({
      ...clientForm,
      pocs: { ...clientForm.pocs, [department]: employeeId }
    });
  };

  const handleCategorySelect = (category) => {
    if (category === '_custom_') {
      setShowCategoryInput(true);
      setClientForm({ ...clientForm, category: '' });
    } else {
      setClientForm({ ...clientForm, category });
      setShowCategoryInput(false);
    }
  };

  const handleViewClientDetails = async (client) => {
    setSelectedClient(client);
    setShowDetailsModal(true);
  };

  const handleManageUsers = async (client) => {
    try {
      const response = await adminApi.get(`/api/admin/clients/${client._id}/users`);
      setClientUsers(response.data || []);
      setSelectedClient(client);
      setShowUsersModal(true);
    } catch (error) {
      toast.error('Failed to load users');
      console.error('Error:', error);
    }
  };

  const handleCreateUser = async () => {
    if (!userForm.name || !userForm.email || !userForm.password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      await adminApi.post(`/api/admin/clients/${selectedClient._id}/users`, userForm);
      toast.success('Client user created successfully');
      handleManageUsers(selectedClient);
      setUserForm({ name: '', email: '', password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
      console.error('Error:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await adminApi.delete(`/api/admin/clients/${selectedClient._id}/users/${userId}`);
      toast.success('User deleted successfully');
      handleManageUsers(selectedClient);
    } catch (error) {
      toast.error('Failed to delete user');
      console.error('Error:', error);
    }
  };

  const handleResetPassword = async (userId, newPassword) => {
    try {
      await adminApi.post(`/api/admin/clients/${selectedClient._id}/users/${userId}/reset-password`, {
        new_password: newPassword
      });
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error('Failed to reset password');
      console.error('Error:', error);
    }
  };

  const resetClientForm = () => {
    setClientForm({
      name: '',
      retainer: '',
      currency: 'INR',
      category: '',
      departments: [],
      pocs: {},
      color: '',
      company_name: '',
      company_address: '',
      company_state: '',
      company_pin_code: '',
      gstin: '',
      pan: '',
      billing_cycle: 'monthly',
      payment_type: 'postpaid',
      retainer_start_date: '',
      retainer_end_date: '',
      brand_anniversary_date: ''
    });
    setShowCategoryInput(false);
  };

  const openEditModal = (client) => {
    // Now unified - Edit button opens the same ClientDetailsModal
    setSelectedClient(client);
    setShowDetailsModal(true);
  };

  const getCurrencySymbol = (currencyCode) => {
    const currency = currencies.find(c => c.code === currencyCode);
    return currency ? currency.symbol : '₹';
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e._id === empId);
    return emp?.personal?.full_name || emp?.work_email || 'Unknown';
  };

  // Auto-assign colors to existing clients
  const handleAutoAssignColors = async () => {
    try {
      const response = await adminApi.post('/api/admin/clients/auto-assign-colors');
      toast.success(`${response.data.message}`);
      fetchClients();
    } catch (error) {
      toast.error('Failed to auto-assign colors');
      console.error('Error:', error);
    }
  };

  if (loading) {
    return (
      <div className={isMobileView ? 'px-4 py-4 space-y-6' : 'p-8 space-y-6'} data-testid="clients-loading">
        <HeaderSkeleton />
        <CardGridSkeleton count={6} columns={3} />
      </div>
    );
  }

  return (
    <div className={isMobileView ? 'px-4 py-4' : 'p-8'}>
      {/* Header */}
      <div className={`flex items-start justify-between ${isMobileView ? 'flex-col gap-3 mb-4' : 'mb-6'}`}>
        <div>
          <h1 className={`font-bold text-[#1a1a2e] ${isMobileView ? 'text-xl' : 'text-3xl'}`} data-testid="clients-heading">Clients</h1>
          <p className={`text-neutral-600 ${isMobileView ? 'text-sm' : 'mt-1'}`}>Manage client accounts and categories</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/40 mb-6 overflow-x-auto">
        <button
          onClick={() => setActiveTab('clients')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'clients'
              ? 'border-pink-500 text-pink-600'
              : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-white/40'
          }`}
          data-testid="tab-clients"
        >
          <Briefcase className="w-4 h-4" />
          Clients
          <span className="bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full text-xs">{clients.length}</span>
        </button>
        <button
          onClick={() => setActiveTab('matrix')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'matrix'
              ? 'border-pink-500 text-pink-600'
              : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-white/40'
          }`}
          data-testid="tab-matrix"
        >
          <Grid3x3 className="w-4 h-4" />
          Matrix
          <span className="bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full text-xs">{clients.length}</span>
        </button>
        <button
          onClick={() => setActiveTab('distribution')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'distribution'
              ? 'border-pink-500 text-pink-600'
              : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-white/40'
          }`}
          data-testid="tab-distribution"
        >
          <BarChart3 className="w-4 h-4" />
          Distribution
        </button>
        <button
          onClick={() => setActiveTab('archived')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'archived'
              ? 'border-pink-500 text-pink-600'
              : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-white/40'
          }`}
          data-testid="tab-archived"
        >
          <Archive className="w-4 h-4" />
          Archived
          <span className="bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full text-xs">{archivedClients.length}</span>
        </button>
        {expiringRetainers.filter(c => !dismissedExpiring.includes(c.id)).length > 0 && (
          <button
            onClick={() => setActiveTab('clients')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 border-transparent text-yellow-600 hover:text-yellow-700 whitespace-nowrap`}
          >
            <AlertTriangle className="w-4 h-4" />
            <span className="text-xs">Expiring Soon ({expiringRetainers.filter(c => !dismissedExpiring.includes(c.id)).length})</span>
          </button>
        )}
        <button
          onClick={() => setActiveTab('categories')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'categories'
              ? 'border-pink-500 text-pink-600'
              : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-white/40'
          }`}
          data-testid="tab-categories"
        >
          <Tag className="w-4 h-4" />
          Categories
          <span className="bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full text-xs">{managedCategories.length}</span>
        </button>
        <button
          onClick={() => { setActiveTab('duplicates'); if (duplicateGroups.length === 0 && !duplicatesLoading) fetchDuplicates(); }}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'duplicates'
              ? 'border-pink-500 text-pink-600'
              : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-white/40'
          }`}
          data-testid="tab-duplicates"
        >
          <GitMerge className="w-4 h-4" />
          Duplicates
          {duplicateGroups.length > 0 && (
            <span className="bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full text-xs">{duplicateGroups.length}</span>
          )}
        </button>
      </div>

      {/* Distribution Tab Content */}
      {activeTab === 'distribution' && (
        <ClientDistributionManager />
      )}

      {/* Matrix Tab Content — customizable data grid across every client field
          with inline editing (PATCH /api/admin/clients/{id}/field) and named
          column presets. Mirrors the Employees Matrix pattern. */}
      {activeTab === 'matrix' && (
        <ClientDataGrid
          clients={clients}
          onEdit={(client) => {
            setEditingClient(client);
            setClientForm({
              name: client.name || '',
              retainer: client.retainer || '',
              currency: client.currency || 'INR',
              category: client.category || '',
              departments: client.departments || [],
              pocs: client.pocs || {},
              color: client.color || '',
              company_name: client.company_name || '',
              company_address: client.company_address || '',
              company_state: client.company_state || '',
              company_pin_code: client.company_pin_code || '',
              gstin: client.gstin || '',
              pan: client.pan || '',
              billing_cycle: client.billing_cycle || 'monthly',
              payment_type: client.payment_type || 'postpaid',
              retainer_start_date: client.retainer_start_date ? String(client.retainer_start_date).slice(0, 10) : '',
              retainer_end_date: client.retainer_end_date ? String(client.retainer_end_date).slice(0, 10) : '',
              brand_anniversary_date: client.brand_anniversary_date ? String(client.brand_anniversary_date).slice(0, 10) : '',
            });
            setShowClientModal(true);
          }}
        />
      )}

      {/* Categories Tab Content */}
      {activeTab === 'categories' && (
        <div>
          {/* Categories Header */}
          <div className="flex items-center justify-between mb-4">
            <div className={`relative ${isMobileView ? 'flex-1 mr-2' : 'w-64'}`}>
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                placeholder="Search categories..."
                value={categorySearchQuery}
                onChange={(e) => setCategorySearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 text-sm"
                data-testid="search-categories-input"
              />
            </div>
            <button
              onClick={() => {
                setEditingCategory(null);
                setShowCategoryModal(true);
              }}
              className="bg-gradient-to-r from-[#FF3366] to-[#FF3366] text-white px-4 py-2 rounded-xl hover:opacity-90 flex items-center gap-2 text-sm"
              data-testid="new-category-btn"
            >
              <Plus className="w-4 h-4" />
              {isMobileView ? 'New' : 'New Category'}
            </button>
          </div>

          {/* Categories Table */}
          {managedCategories.length === 0 ? (
            <div className="soft-card">
              <EmptyState
                icon={FolderOpen}
                title="No categories yet"
                description="Create categories to organize your clients."
                actionLabel="Create Category"
                onAction={() => { setEditingCategory(null); setShowCategoryModal(true); }}
                size="large"
                testId="empty-categories"
              />
            </div>
          ) : (
            <div className="soft-card overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full" style={{ minWidth: '500px' }}>
                <thead className="bg-white/40 border-b border-white/40">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Name</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Description</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Clients</th>
                    <th className="text-right px-6 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/40">
                  {managedCategories
                    .filter(cat => cat.name.toLowerCase().includes(categorySearchQuery.toLowerCase()))
                    .map((category) => {
                      const clientCount = clients.filter(c => c.category === category.name).length;
                      return (
                        <tr key={category._id} className="hover:bg-white/60" data-testid={`category-row-${category._id}`}>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Tag className="w-4 h-4 text-pink-500" />
                              <span className="font-medium text-[#1a1a2e]">{category.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-neutral-500">
                            {category.description || <span className="text-neutral-400 italic">No description</span>}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              clientCount > 0 ? 'bg-blue-100 text-blue-800' : 'bg-neutral-100 text-neutral-600'
                            }`}>
                              {clientCount} client{clientCount !== 1 ? 's' : ''}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => {
                                  setEditingCategory(category);
                                  setShowCategoryModal(true);
                                }}
                                className="p-1.5 text-neutral-500 hover:text-pink-600 hover:bg-pink-50 rounded-xl transition-colors"
                                data-testid={`edit-category-${category._id}`}
                                title="Edit category"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteCategory(category)}
                                className="p-1.5 text-neutral-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                                data-testid={`delete-category-${category._id}`}
                                title="Delete category"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Clients Tab Content */}
      {activeTab === 'clients' && (
        <>
          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2 mb-4">
            <button 
              onClick={handleAutoAssignColors}
              className={`border border-white/40 text-neutral-700 rounded-xl hover:bg-white/60 flex items-center gap-2 ${isMobileView ? 'px-3 py-2 text-sm' : 'px-4 py-2'}`}
              data-testid="auto-assign-colors-btn"
              title="Auto-assign colors to clients without colors"
            >
              <Palette className="w-4 h-4" />
              {!isMobileView && 'Assign Colors'}
            </button>
            <button 
              onClick={() => {
                setEditingClient(null);
                resetClientForm();
                setShowClientModal(true);
              }}
              className={`bg-gradient-to-r from-[#FF3366] to-[#FF3366] text-white rounded-xl hover:opacity-90 flex items-center gap-2 ${isMobileView ? 'px-3 py-2 text-sm' : 'px-4 py-2'}`}
              data-testid="new-client-btn"
            >
              <Plus className="w-4 h-4" />
              {isMobileView ? 'New' : 'New Client'}
            </button>
          </div>

          {/* Expiring Retainers Alert */}
          <ExpiringRetainersAlert
            clients={expiringRetainers.filter(c => !dismissedExpiring.includes(c.id))}
            onArchive={(client) => {
              setArchiveClient(client);
              setShowArchiveModal(true);
            }}
            onDismiss={(clientId) => setDismissedExpiring(prev => [...prev, clientId])}
          />

          {/* Search and Filter */}
          <div className={`mb-4 ${isMobileView ? 'space-y-3' : 'flex items-center gap-4 mb-6'}`}>
            <div className={`relative ${isMobileView ? 'w-full' : 'flex-1'}`}>
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className={`w-full pl-10 pr-4 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 ${isMobileView ? 'py-2.5' : 'py-3'}`}
                data-testid="search-clients-input"
              />
            </div>
            <div className={`flex gap-2 ${isMobileView ? '' : ''}`}>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className={`flex-1 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 ${isMobileView ? 'px-3 py-2.5 text-sm' : 'px-4 py-3'}`}
                data-testid="category-filter-select"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <button 
                onClick={handleSearch}
                className={`bg-neutral-100 text-neutral-700 rounded-xl hover:bg-neutral-200 font-medium ${isMobileView ? 'px-4 py-2.5 text-sm' : 'px-6 py-3'}`}
                data-testid="search-btn"
              >
                Search
          </button>
        </div>
      </div>

      {/* Clients Grid */}
      {clients.length === 0 ? (
        <div className="glass-card">
          <EmptyState
            icon={Briefcase}
            title="No clients found"
            description="Get started by creating your first client."
            actionLabel="Create First Client"
            onAction={() => { setEditingClient(null); resetClientForm(); setShowClientModal(true); }}
            size="large"
            testId="empty-clients"
          />
        </div>
      ) : (
        <div className={`grid gap-4 ${isMobileView ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'}`}>
          {clients.map((client) => (
            <div 
              key={client._id} 
              className="soft-card p-6 hover:shadow-lg transition-shadow relative overflow-hidden" 
              data-testid={`client-card-${client._id}`}
              style={{ borderLeft: `4px solid ${client.color || '#E5E7EB'}` }}
            >
              {/* Color indicator bar at top */}
              <div 
                className="absolute top-0 left-0 right-0 h-1" 
                style={{ backgroundColor: client.color || '#E5E7EB' }}
              />
              
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <ClientColorBadge color={client.color} size="md" showName={false} />
                    <h3 className="text-lg font-bold text-[#1a1a2e]">{client.name}</h3>
                  </div>
                  <p className="text-sm text-neutral-600">{client.category || 'No Category'}</p>
                  {client.retainer && (
                    <p className="text-sm font-semibold text-green-600 mt-2" data-testid="retainer-display">
                      Retainer: {getCurrencySymbol(client.currency)}{client.retainer.toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditModal(client)}
                    className="text-pink-600 hover:text-pink-700 p-1"
                    title="Edit client"
                    data-testid={`edit-client-${client._id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setArchiveClient(client);
                      setShowArchiveModal(true);
                    }}
                    className="text-orange-600 hover:text-orange-700 p-1"
                    title="Archive client"
                    data-testid={`archive-client-${client._id}`}
                  >
                    <Archive className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteClient(client._id)}
                    className="text-red-600 hover:text-red-700 p-1"
                    title="Delete client"
                    data-testid={`delete-client-${client._id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <Briefcase className="w-4 h-4" />
                  <span>{client.task_count || 0} Tasks</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <Users className="w-4 h-4" />
                  <span>{client.user_count || 0} Users</span>
                </div>
                {client.departments && client.departments.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {client.departments.map((dept) => (
                      <span key={dept} className="px-2 py-1 bg-pink-100 text-pink-700 rounded text-xs">
                        {dept}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleViewClientDetails(client)}
                  className="flex-1 px-4 py-2 bg-neutral-100 text-neutral-700 rounded-xl hover:bg-neutral-200 text-sm font-medium flex items-center justify-center gap-2"
                  data-testid={`view-details-${client._id}`}
                >
                  <Eye className="w-4 h-4" />
                  Details
                </button>
                <button
                  onClick={() => handleManageUsers(client)}
                  className="flex-1 px-4 py-2 bg-pink-100 text-pink-700 rounded-xl hover:bg-pink-200 text-sm font-medium flex items-center justify-center gap-2"
                  data-testid={`manage-users-${client._id}`}
                >
                  <UserPlus className="w-4 h-4" />
                  Users
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
        </>
      )}

      {/* Archived Clients Tab Content */}
      {activeTab === 'archived' && (
        <div>
          <div className="mb-4">
            <p className="text-neutral-600">
              Archived clients are hidden from active lists but their data is preserved. You can restore them at any time.
            </p>
          </div>
          
          {archivedClients.length === 0 ? (
            <div className="bg-white/40 border border-white/40 rounded-xl p-8 text-center">
              <Archive className="w-12 h-12 text-neutral-400 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-neutral-700">No Archived Clients</h3>
              <p className="text-neutral-500 mt-1">Clients you archive will appear here.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {archivedClients.map((client) => (
                <div 
                  key={client.id} 
                  className="soft-card rounded-xl p-4 opacity-75 hover:opacity-100 transition-opacity"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {client.color && (
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: client.color }}
                        />
                      )}
                      <div>
                        <h3 className="font-semibold text-neutral-700">{client.company_name || client.name}</h3>
                        {client.category && (
                          <span className="text-xs text-neutral-500">{client.category}</span>
                        )}
                      </div>
                    </div>
                    <Archive className="w-4 h-4 text-neutral-400" />
                  </div>
                  
                  <div className="space-y-2 text-sm text-neutral-500 mb-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>Archived: {client.archived_at ? formatDate(client.archived_at) : 'N/A'}</span>
                    </div>
                    <div>
                      <span className="font-medium">Reason:</span> {client.archived_reason?.replace(/_/g, ' ') || 'Not specified'}
                    </div>
                    {client.on_hold_tasks > 0 && (
                      <div className="text-orange-600">
                        {client.on_hold_tasks} task{client.on_hold_tasks !== 1 ? 's' : ''} on hold
                      </div>
                    )}
                    {client.archive_notes && (
                      <div className="text-neutral-600 italic text-xs bg-white/40 p-2 rounded">
                        &quot;{client.archive_notes}&quot;
                      </div>
                    )}
                  </div>

                  {/* Archive stats */}
                  {client.archive_stats && (
                    <div className="grid grid-cols-2 gap-2 text-xs text-neutral-500 mb-4 p-2 bg-white/40 rounded">
                      <div>Tasks: {client.archive_stats.completed_tasks || 0}/{client.archive_stats.total_tasks || 0}</div>
                      <div>Invoiced: ₹{(client.archive_stats.total_invoiced || 0).toLocaleString()}</div>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setRestoreClient(client);
                        setShowRestoreModal(true);
                      }}
                      className="flex-1 px-3 py-2 bg-green-50 text-green-600 border border-green-200 rounded-xl hover:bg-green-100 flex items-center justify-center gap-2 text-sm"
                      data-testid={`restore-client-${client.id}`}
                    >
                      <ArchiveRestore className="w-4 h-4" />
                      Restore
                    </button>
                    <button
                      onClick={() => handleDeleteClient(client.id)}
                      className="px-3 py-2 text-red-600 border border-red-200 rounded-xl hover:bg-red-50"
                      title="Delete permanently"
                      data-testid={`delete-archived-client-${client.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Duplicates Tab Content */}
      {activeTab === 'duplicates' && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="text-neutral-600">
                Potential duplicate clients detected by matching GSTIN or normalized names. Review and merge to maintain clean data.
              </p>
            </div>
            <button
              onClick={fetchDuplicates}
              disabled={duplicatesLoading}
              className="px-4 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700 disabled:opacity-50 flex items-center gap-2 text-sm"
              data-testid="refresh-duplicates-btn"
            >
              {duplicatesLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitMerge className="w-4 h-4" />}
              {duplicatesLoading ? 'Scanning...' : 'Re-scan'}
            </button>
          </div>

          {duplicatesLoading ? (
            <div className="glass-card rounded-xl p-12 text-center">
              <Loader2 className="w-8 h-8 text-pink-500 animate-spin mx-auto mb-3" />
              <p className="text-neutral-500">Scanning for duplicates...</p>
            </div>
          ) : duplicateGroups.length === 0 ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
              <Shield className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-green-700" data-testid="no-duplicates-message">No Duplicates Found</h3>
              <p className="text-green-600 mt-1">Your client database is clean. No potential duplicates detected.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {duplicateGroups.map((group, groupIdx) => (
                <div key={groupIdx} className="glass-card rounded-xl overflow-hidden" data-testid={`duplicate-group-${groupIdx}`}>
                  <div className={`px-4 py-3 flex items-center gap-3 ${
                    group.confidence === 'high' ? 'bg-red-50 border-b border-red-200' : 'bg-yellow-50 border-b border-yellow-200'
                  }`}>
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                      group.confidence === 'high' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {group.confidence === 'high' ? 'HIGH' : 'MEDIUM'} CONFIDENCE
                    </span>
                    <span className="text-sm text-neutral-600">
                      Match: <span className="font-medium">{group.match_type === 'gstin' ? 'Same GSTIN' : 'Similar Name'}</span>
                      {' '}&mdash; <code className="text-xs bg-neutral-100 px-1 py-0.5 rounded">{group.match_value}</code>
                    </span>
                  </div>
                  
                  <div className="p-4">
                    <div className={`grid gap-4 ${group.clients.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-3'}`}>
                      {group.clients.map((client) => (
                        <div key={client.id} className="border border-white/40 rounded-xl p-4 relative">
                          <div className="mb-3">
                            <h4 className="font-semibold text-[#1a1a2e]">{client.company_name || client.name}</h4>
                            {client.company_name && client.name !== client.company_name && (
                              <p className="text-xs text-neutral-500">({client.name})</p>
                            )}
                          </div>
                          <div className="space-y-1.5 text-sm text-neutral-600">
                            <div className="flex justify-between">
                              <span>Status:</span>
                              <span className={`font-medium ${client.is_active ? 'text-green-600' : 'text-neutral-500'}`}>
                                {client.is_archived ? 'Archived' : client.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            {client.gstin && <div className="flex justify-between"><span>GSTIN:</span><span className="font-mono text-xs">{client.gstin}</span></div>}
                            {client.category && <div className="flex justify-between"><span>Category:</span><span>{client.category}</span></div>}
                            <div className="flex justify-between"><span>Tasks:</span><span className="font-medium">{client.task_count}</span></div>
                            <div className="flex justify-between"><span>Invoices:</span><span className="font-medium">{client.invoice_count}</span></div>
                            {client.source && <div className="flex justify-between"><span>Source:</span><span className="text-xs">{client.source}</span></div>}
                          </div>
                          
                          {/* Merge buttons - for each OTHER client in the group */}
                          <div className="mt-4 space-y-2">
                            {group.clients.filter(c => c.id !== client.id).map(otherClient => (
                              <button
                                key={otherClient.id}
                                onClick={() => handleMergeClients(otherClient.id, client.id)}
                                disabled={mergingPair !== null}
                                className="w-full px-3 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-xl hover:bg-blue-100 flex items-center justify-center gap-2 text-xs font-medium disabled:opacity-50"
                                data-testid={`merge-into-${client.id}-from-${otherClient.id}`}
                              >
                                {mergingPair?.source_id === otherClient.id && mergingPair?.target_id === client.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <ArrowRight className="w-3 h-3" />
                                )}
                                Keep this, merge &quot;{(otherClient.company_name || otherClient.name).substring(0, 20)}&quot; into it
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Client Modal - Used only for NEW clients */}
      {showClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto overflow-x-visible">
            <div className="sticky top-0 bg-white border-b border-white/40 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-2xl font-bold text-[#1a1a2e]">
                Create New Client
              </h2>
              <button onClick={() => setShowClientModal(false)} className="text-neutral-500 hover:text-neutral-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6 overflow-visible">
              {/* Client Name */}
              <div>
                <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                  Client Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={clientForm.name}
                  onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                  placeholder="Enter client name"
                  data-testid="client-name-input"
                />
              </div>

              {/* Retainer with Currency */}
              <div>
                <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                  Retainer (Admin Only)
                </label>
                <div className="flex gap-3">
                  <select
                    value={clientForm.currency}
                    onChange={(e) => setClientForm({ ...clientForm, currency: e.target.value })}
                    className="w-32 px-3 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30 bg-white"
                    data-testid="client-currency-select"
                  >
                    {currencies.map((curr) => (
                      <option key={curr.code} value={curr.code}>
                        {curr.symbol} {curr.code}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={clientForm.retainer}
                    onChange={(e) => setClientForm({ ...clientForm, retainer: e.target.value })}
                    className="flex-1 px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                    placeholder="Enter retainer amount"
                    data-testid="client-retainer-input"
                  />
                </div>
                <p className="text-xs text-neutral-500 mt-1">This field is only visible to admins</p>
              </div>

              {/* Billing & Payment Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                    Billing Cycle
                  </label>
                  <select
                    value={clientForm.billing_cycle}
                    onChange={(e) => setClientForm({ ...clientForm, billing_cycle: e.target.value })}
                    className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30 bg-white"
                    data-testid="client-billing-cycle"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                    Payment Type
                  </label>
                  <select
                    value={clientForm.payment_type}
                    onChange={(e) => setClientForm({ ...clientForm, payment_type: e.target.value })}
                    className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30 bg-white"
                    data-testid="client-payment-type"
                  >
                    <option value="advance">Advance</option>
                    <option value="postpaid">Postpaid</option>
                  </select>
                </div>
              </div>

              {/* Retainer Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                    Retainer Start Date
                  </label>
                  <input
                    type="date"
                    value={clientForm.retainer_start_date}
                    onChange={(e) => setClientForm({ ...clientForm, retainer_start_date: e.target.value })}
                    className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                    data-testid="client-start-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                    Retainer End Date
                  </label>
                  <input
                    type="date"
                    value={clientForm.retainer_end_date}
                    onChange={(e) => setClientForm({ ...clientForm, retainer_end_date: e.target.value })}
                    className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                    data-testid="client-end-date"
                  />
                </div>
              </div>

              {/* Brand Anniversary — feeds the 60-day advance campaign heads-up */}
              <div>
                <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                  🎉 Brand Anniversary
                  <span className="ml-2 text-xs font-normal text-neutral-500">
                    Notifies account manager + admins 60 days before every year
                  </span>
                </label>
                <input
                  type="date"
                  value={clientForm.brand_anniversary_date}
                  onChange={(e) => setClientForm({ ...clientForm, brand_anniversary_date: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                  data-testid="client-brand-anniversary-date"
                  placeholder="YYYY-MM-DD"
                />
              </div>

              {/* Company Details Section */}
              <div className="border-t border-white/40 pt-4 mt-4">
                <h3 className="text-lg font-semibold text-[#1a1a2e] mb-4">Company Details (For Invoicing)</h3>
                
                <div className="space-y-4">
                  {/* Company Name */}
                  <div>
                    <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                      Legal Company Name
                    </label>
                    <input
                      type="text"
                      value={clientForm.company_name}
                      onChange={(e) => setClientForm({ ...clientForm, company_name: e.target.value })}
                      className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                      placeholder="Enter legal company name"
                      data-testid="client-company-name"
                    />
                  </div>

                  {/* Company Address */}
                  <div>
                    <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                      Company Address
                    </label>
                    <textarea
                      value={clientForm.company_address}
                      onChange={(e) => setClientForm({ ...clientForm, company_address: e.target.value })}
                      className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                      placeholder="Enter company address"
                      rows={2}
                      data-testid="client-company-address"
                    />
                  </div>

                  {/* State and PIN Code - CRITICAL for GST calculation */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                        State <span className="text-red-500">*</span>
                        <span className="text-xs font-normal text-neutral-500 ml-1">(for GST)</span>
                      </label>
                      <select
                        value={clientForm.company_state}
                        onChange={(e) => setClientForm({ ...clientForm, company_state: e.target.value })}
                        className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                        data-testid="client-company-state"
                      >
                        <option value="">Select State</option>
                        {indianStates.map((state) => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                      {clientForm.company_state && (
                        <p className={`text-xs mt-1 ${clientForm.company_state === 'Karnataka' ? 'text-green-600' : 'text-blue-600'}`}>
                          {clientForm.company_state === 'Karnataka' 
                            ? '📍 Intra-state: CGST + SGST will apply' 
                            : '📍 Inter-state: IGST will apply'}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                        PIN Code
                      </label>
                      <input
                        type="text"
                        value={clientForm.company_pin_code}
                        onChange={(e) => setClientForm({ ...clientForm, company_pin_code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                        className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                        placeholder="e.g., 560001"
                        maxLength={6}
                        data-testid="client-company-pin-code"
                      />
                    </div>
                  </div>

                  {/* GSTIN and PAN */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                        GSTIN
                      </label>
                      <input
                        type="text"
                        value={clientForm.gstin}
                        onChange={(e) => setClientForm({ ...clientForm, gstin: e.target.value.toUpperCase() })}
                        className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                        placeholder="e.g., 22AAAAA0000A1Z5"
                        maxLength={15}
                        data-testid="client-gstin"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                        PAN
                      </label>
                      <input
                        type="text"
                        value={clientForm.pan}
                        onChange={(e) => setClientForm({ ...clientForm, pan: e.target.value.toUpperCase() })}
                        className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                        placeholder="e.g., AAAAA0000A"
                        maxLength={10}
                        data-testid="client-pan"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    value={clientForm.category}
                    onChange={(e) => setClientForm({ ...clientForm, category: e.target.value })}
                    className="flex-1 px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                    data-testid="client-category-select"
                  >
                    <option value="">Select category</option>
                    {managedCategories.map((cat) => (
                      <option key={cat._id} value={cat.name}>{cat.name}</option>
                    ))}
                    {/* Also show legacy categories that aren't in managed list */}
                    {categories.filter(c => !managedCategories.some(mc => mc.name === c)).map((cat) => (
                      <option key={cat} value={cat}>{cat} (legacy)</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingCategory(null);
                      setShowCategoryModal(true);
                    }}
                    className="px-3 py-2 border border-white/40 rounded-xl hover:bg-white/60 text-neutral-600"
                    title="Add new category"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  Manage categories in the <button type="button" onClick={() => setActiveTab('categories')} className="text-pink-600 hover:underline">Categories tab</button>
                </p>
              </div>

              {/* Departments with POCs */}
              <div>
                <label className="block text-sm font-semibold text-[#1a1a2e] mb-3">
                  Departments & Default POCs
                </label>
                <div className="space-y-3 max-h-64 overflow-y-auto border border-white/40 rounded-xl p-4">
                  {departments.length === 0 ? (
                    <p className="text-neutral-500 text-sm">No departments available</p>
                  ) : (
                    departments.map((dept) => {
                      // Filter employees by department name (departments are now stored as names)
                      const deptEmployees = employees.filter(emp => {
                        const empDepts = emp.departments || emp.employment?.departments || [];
                        return empDepts.includes(dept.name);
                      });
                      
                      return (
                        <div key={dept._id || dept.name} className="flex items-center gap-4">
                          <input
                            type="checkbox"
                            checked={clientForm.departments.includes(dept.name)}
                            onChange={() => handleDepartmentToggle(dept.name)}
                            className="w-4 h-4 text-pink-600 focus:ring-pink-500 rounded"
                            data-testid={`dept-checkbox-${dept.name}`}
                          />
                          <span className="flex-1 text-sm font-medium text-[#1a1a2e]">{dept.name}</span>
                          {clientForm.departments.includes(dept.name) && (
                            <div className="w-56">
                              <EmployeeSearchDropdown
                                employees={deptEmployees.length > 0 ? deptEmployees : employees}
                                value={clientForm.pocs[dept.name] || ''}
                                onChange={(empId) => handlePOCSelect(dept.name, empId)}
                                placeholder={deptEmployees.length > 0 ? "Select POC..." : "Select POC (all employees)"}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                <p className="text-xs text-neutral-500 mt-2">
                  Select departments and assign a default Point of Contact for each
                </p>
              </div>

              {/* Client Color Picker */}
              <div>
                <label className="block text-sm font-semibold text-[#1a1a2e] mb-3">
                  Client Color
                </label>
                <div className="grid grid-cols-10 gap-2 p-3 border border-white/40 rounded-xl bg-white/40">
                  {CLIENT_COLOR_PALETTE.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setClientForm({ ...clientForm, color })}
                      className={`w-8 h-8 rounded-xl transition-all ${
                        clientForm.color === color 
                          ? 'ring-2 ring-offset-2 ring-gray-900 scale-110' 
                          : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                      data-testid={`color-picker-${color}`}
                    />
                  ))}
                </div>
                {clientForm.color && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-sm text-neutral-600">Selected:</span>
                    <span 
                      className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium"
                      style={{ 
                        backgroundColor: getLighterColor(clientForm.color, 0.2),
                        color: clientForm.color
                      }}
                    >
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: clientForm.color }} />
                      {clientForm.color}
                    </span>
                    <button
                      type="button"
                      onClick={() => setClientForm({ ...clientForm, color: '' })}
                      className="text-neutral-400 hover:text-neutral-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <p className="text-xs text-neutral-500 mt-2">
                  Choose a color to identify this client across the app. Leave empty for auto-assignment.
                </p>
              </div>

              {/* Submit Buttons */}
              <div className="flex items-center justify-end gap-4 pt-4 border-t border-white/40">
                <button
                  onClick={() => setShowClientModal(false)}
                  className="px-6 py-3 border border-white/40 rounded-xl text-neutral-700 hover:bg-white/60 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateClient}
                  className="px-6 py-3 bg-gradient-to-r from-[#FF3366] to-[#FF3366] text-white rounded-xl hover:opacity-90 font-medium"
                  data-testid="submit-client-btn"
                >
                  Create Client
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Users Modal */}
      {showUsersModal && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-white/40 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-[#1a1a2e]">Manage Users</h2>
                <p className="text-sm text-neutral-600">{selectedClient.name}</p>
              </div>
              <button onClick={() => setShowUsersModal(false)} className="text-neutral-500 hover:text-neutral-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {/* Add New User Form */}
              <div className="bg-white/40 rounded-xl p-4 mb-6">
                <h3 className="font-semibold text-[#1a1a2e] mb-4">Add New User</h3>
                <div className="grid grid-cols-3 gap-3">
                  <input
                    type="text"
                    placeholder="Name"
                    value={userForm.name}
                    onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                    className="px-3 py-2 border border-white/40 rounded-xl text-sm"
                    data-testid="user-name-input"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    className="px-3 py-2 border border-white/40 rounded-xl text-sm"
                    data-testid="user-email-input"
                  />
                  <input
                    type="password"
                    placeholder="Password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    className="px-3 py-2 border border-white/40 rounded-xl text-sm"
                    data-testid="user-password-input"
                  />
                </div>
                <button
                  onClick={handleCreateUser}
                  className="mt-3 px-4 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700 text-sm font-medium"
                  data-testid="add-user-btn"
                >
                  Add User
                </button>
              </div>

              {/* Existing Users List */}
              <div>
                <h3 className="font-semibold text-[#1a1a2e] mb-4">Client Users ({clientUsers.length})</h3>
                {clientUsers.length === 0 ? (
                  <p className="text-neutral-500 text-sm text-center py-8">No users added yet</p>
                ) : (
                  <div className="space-y-3">
                    {clientUsers.map((user) => (
                      <div key={user._id} className="flex items-center justify-between glass-card rounded-xl p-4">
                        <div>
                          <p className="font-semibold text-[#1a1a2e]">{user.name}</p>
                          <p className="text-sm text-neutral-600">{user.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              const newPassword = prompt('Enter new password for ' + user.name);
                              if (newPassword && newPassword.length >= 6) {
                                handleResetPassword(user._id, newPassword);
                              } else if (newPassword) {
                                toast.error('Password must be at least 6 characters');
                              }
                            }}
                            className="text-yellow-600 hover:text-yellow-700 p-2"
                            title="Reset Password"
                            data-testid={`reset-password-${user._id}`}
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user._id)}
                            className="text-red-600 hover:text-red-700 p-2"
                            title="Delete User"
                            data-testid={`delete-user-${user._id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Client Details Modal - Enhanced */}
      <ClientDetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedClient(null);
        }}
        clientId={selectedClient?._id}
        employees={employees}
        departments={departments}
        categories={categories}
        onSave={() => {
          fetchClients();
          fetchCategories();
        }}
        onCategoriesUpdate={fetchCategories}
      />

      {/* Category Modal */}
      <CategoryModal
        isOpen={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setEditingCategory(null);
        }}
        category={editingCategory}
        onSave={handleSaveCategory}
      />

      {/* Archive Client Modal */}
      <ClientArchiveModal
        client={archiveClient}
        isOpen={showArchiveModal}
        onClose={() => {
          setShowArchiveModal(false);
          setArchiveClient(null);
        }}
        onComplete={() => {
          fetchClients();
          fetchArchivedClients();
          fetchExpiringRetainers();
        }}
      />

      {/* Restore Client Modal */}
      <ClientRestoreModal
        client={restoreClient}
        isOpen={showRestoreModal}
        onClose={() => {
          setShowRestoreModal(false);
          setRestoreClient(null);
        }}
        onComplete={() => {
          fetchClients();
          fetchArchivedClients();
        }}
      />
    </div>
  );
}
